import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useSpeechRecognition
 *
 * Wraps the native Web Speech API to provide:
 *  - isListening  – whether the mic is active
 *  - isSupported  – whether the browser supports SpeechRecognition
 *  - startListening(onTranscript, onEnd) – start mic; callbacks fire with live text / final text
 *  - stopListening() – abort the session
 */
const useSpeechRecognition = () => {
    const [isListening, setIsListening] = useState(false);

    const recognitionRef = useRef(null);
    const onTranscriptRef = useRef(null);
    const onEndRef = useRef(null);

    // Detect browser support once
    const SpeechRecognitionAPI =
        window.SpeechRecognition || window.webkitSpeechRecognition;
    const isSupported = Boolean(SpeechRecognitionAPI);

    // Build the recognition instance lazily
    const getRecognition = useCallback(() => {
        if (recognitionRef.current) return recognitionRef.current;
        if (!SpeechRecognitionAPI) return null;

        const recognition = new SpeechRecognitionAPI();
        recognition.continuous = false;
        recognition.interimResults = true;
        recognition.lang = "en-US";

        recognition.onresult = (event) => {
            const transcript = Array.from(event.results)
                .map((r) => r[0].transcript)
                .join("");
            onTranscriptRef.current?.(transcript);
        };

        recognition.onend = () => {
            setIsListening(false);
            onEndRef.current?.();
        };

        recognition.onerror = (event) => {
            console.error("[SpeechRecognition] error:", event.error);
            setIsListening(false);
        };

        recognitionRef.current = recognition;
        return recognition;
    }, [SpeechRecognitionAPI]);

    const startListening = useCallback(
        (onTranscript, onEnd) => {
            const recognition = getRecognition();
            if (!recognition) return;

            onTranscriptRef.current = onTranscript;
            onEndRef.current = onEnd;

            try {
                recognition.start();
                setIsListening(true);
            } catch (err) {
                // Guard against "already started" errors on rapid clicks
                console.warn("[SpeechRecognition] start error:", err);
            }
        },
        [getRecognition]
    );

    const stopListening = useCallback(() => {
        recognitionRef.current?.stop();
        setIsListening(false);
    }, []);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            recognitionRef.current?.abort();
        };
    }, []);

    return { isListening, isSupported, startListening, stopListening };
};

export default useSpeechRecognition;
